import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FaDumbbell, FaChartLine, FaCalendarAlt, FaFire, FaPlay, 
  FaCheck, FaArrowLeft, FaClock, FaTimes, FaSave,
  FaChevronDown, FaChevronUp, FaTrophy, FaStopwatch
} from 'react-icons/fa';
import { useGender } from '@/context/GenderContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRAINING_METHODS = {
  'normal': { name: 'Normal', description: 'Ejecución estándar con control', color: '#6B7280', icon: '' },
  'dropset': { name: 'Dropset', description: 'Reducción progresiva del peso sin descanso entre series', color: '#A855F7', icon: '⬇' },
  'rest_pause': { name: 'Rest-Pause', description: 'Realizar repeticiones hasta el fallo, descansar brevemente y continuar', color: '#F97316', icon: '⏸' },
  'cluster': { name: 'Cluster Sets', description: 'Series divididas en mini-sets con descansos cortos intra-serie', color: '#22C55E', icon: '🔗' },
  'cluster sets': { name: 'Cluster Sets', description: 'Series con mini-descansos de 10-20 seg entre grupos de reps', color: '#22C55E', icon: '🔗' },
  'myo_reps': { name: 'Myo-Reps', description: 'Serie de activación seguida de mini-series con descansos muy cortos', color: 'var(--brand-primary)', icon: '🔄' },
  'myo-reps': { name: 'Myo-Reps', description: 'Serie hasta el fallo, descanso 3-5 seg, mini-series de 3-5 reps', color: 'var(--brand-primary)', icon: '🔄' },
  'tempo_controlado': { name: 'Tempo', description: 'Control del tiempo en cada fase del movimiento', color: '#3B82F6', icon: '⏱' },
  'pausas_isometricas': { name: 'Isométrica', description: 'Pausas en puntos específicos del recorrido', color: '#06B6D4', icon: '⏹' },
  'excentrica_lenta': { name: 'Excéntrica', description: 'Fase excéntrica muy controlada y lenta', color: '#6366F1', icon: '↓' },
  'back_off_set': { name: 'Back-Off', description: 'Serie final con peso reducido para más volumen', color: '#F59E0B', icon: '↩' },
  'super_serie': { name: 'Super Serie', description: 'Dos ejercicios seguidos sin descanso', color: '#8B5CF6', icon: '⚡' },
  'amrap': { name: 'AMRAP', description: 'As Many Reps As Possible', color: '#10B981', icon: '∞' },
  'emom': { name: 'EMOM', description: 'Every Minute On the Minute', color: '#0EA5E9', icon: '⏰' },
  'drop set': { name: 'Drop Set', description: 'Al fallar, reduce el peso 20-30% y continúa sin descanso', color: '#A855F7', icon: '⬇' },
  'rest-pause': { name: 'Rest-Pause', description: 'Al fallar, descansa 10-15 seg y haz más reps', color: '#F97316', icon: '⏸' },
  'fst-7': { name: 'FST-7', description: '7 series con 30 seg de descanso para máximo bombeo', color: '#EF4444', icon: '7️' },
};

// Add tempo variants
['tempo 3-1-3','tempo 2-1-2','tempo 4-0-2','tempo 3-2-3','tempo 3-3-3','tempo 4-2-2','tempo 3-2-1'].forEach(k => {
  TRAINING_METHODS[k] = { name: k.replace('tempo ', 'Tempo '), description: `Tempo controlado: ${k.replace('tempo ', '')}`, color: '#3B82F6', icon: '⏱' };
});

const getMethodInfo = (technique) => {
  if (!technique) return TRAINING_METHODS['normal'];
  const key = technique.toLowerCase();
  return TRAINING_METHODS[key] || { name: technique, description: 'Técnica especial', color: '#6B7280', icon: '★' };
};

// Determinar cantidad de drops en un dropset
const getDropCount = (methodNotes) => {
  if (!methodNotes) return 2;
  const lower = methodNotes.toLowerCase();
  if (lower.includes('triple') || lower.includes('triples')) return 3;
  if (lower.includes('cuadruple') || lower.includes('cuádruples') || lower.includes('cuadruples')) return 4;
  if (lower.includes('doble') || lower.includes('dobles')) return 2;
  if (lower.includes('quíntuple') || lower.includes('quintuples')) return 5;
  return 2;
};

// Configuración de sub-series para técnicas avanzadas
const getSubSetConfig = (method, methodNotes) => {
  const lower = (methodNotes || '').toLowerCase();
  const methodLower = (method || '').toLowerCase();
  
  // Drop Sets: peso diferente en cada sub-serie
  if (methodLower === 'dropset' || methodLower === 'drop set' || methodLower === 'drop_set') {
    const count = getDropCount(methodNotes);
    return { count, weightPerSub: true, labels: ['Peso', ...Array(count - 1).fill(0).map((_, i) => `Drop ${i + 1}`)] };
  }
  
  // Rest-Pause: mismo peso, múltiples mini-series
  if (methodLower === 'rest_pause' || methodLower === 'rest-pause') {
    let count = 3;
    if (lower.includes('4 mini') || lower.includes('cuadruple')) count = 5;
    if (lower.includes('3 mini') || lower.includes('triple')) count = 4;
    return { count, weightPerSub: false, labels: ['Serie', ...Array(count - 1).fill(0).map((_, i) => `Mini ${i + 1}`)] };
  }
  
  // Myo-Reps: serie de activación + mini-series
  if (methodLower === 'myo_reps' || methodLower === 'myo-reps') {
    let count = 4;
    if (lower.includes('5') || lower.includes('cinco')) count = 6;
    return { count, weightPerSub: false, labels: ['Activación', ...Array(count - 1).fill(0).map((_, i) => `Mini ${i + 1}`)] };
  }
  
  // Cluster Sets: mini-sets con descansos cortos
  if (methodLower === 'cluster' || methodLower === 'cluster sets' || methodLower === 'cluster_sets') {
    let count = 3;
    if (lower.includes('4') || lower.includes('cuatro')) count = 4;
    if (lower.includes('5') || lower.includes('cinco')) count = 5;
    return { count, weightPerSub: false, labels: Array.from({ length: count }, (_, i) => `Cluster ${i + 1}`) };
  }
  
  return null; // Técnica normal, sin sub-series
};

const REST_LABELS = {
  '30': '0:30', '45': '0:45', '60': '1:00', '75': '1:15',
  '90': '1:30', '120': '2:00', '150': '2:30', '180': '3:00',
  '240': '4:00', '300': '5:00'
};

// 2026-02 · Botón "¿No puedes hacerlo? Ver alternativas" por ejercicio.
// Solo se renderiza si el ejercicio trae `alternatives: [...]` desde el admin.
// Al elegir una alternativa llama al endpoint `swap-exercise` que persiste
// el cambio en la rutina del usuario.
const AlternativeExerciseSwap = ({ exercise, exIndex, dayIndex, onSwapped }) => {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const alts = (exercise?.alternatives || []).filter(Boolean);
  if (alts.length === 0) return null;
  const chooseAlternative = async (altName) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/workout-plan/swap-exercise`,
        {
          exercise_index: exIndex,
          new_exercise_name: altName,
          day_index: typeof dayIndex === 'number' ? dayIndex : null,
        },
        { headers }
      );
      toast.success(`Ejercicio cambiado a "${altName}"`);
      onSwapped?.(altName);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reemplazar el ejercicio');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-2 pl-[52px]" data-testid={`alt-swap-wrap-${exIndex}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="text-[11px] text-cyan-300 hover:text-cyan-100 underline"
        data-testid={`alt-swap-toggle-${exIndex}`}
      >
        🔁 ¿No puedes hacerlo? Ver alternativas ({alts.length})
      </button>
      {open && (
        <div className="mt-2 p-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5" data-testid={`alt-swap-panel-${exIndex}`}>
          <p className="text-[10px] text-cyan-200 mb-1 uppercase tracking-wider font-semibold">Elige un reemplazo</p>
          <div className="space-y-1">
            {alts.map((a, i) => (
              <button
                key={i}
                disabled={saving}
                onClick={() => chooseAlternative(a)}
                className="w-full text-left px-2 py-1.5 text-xs rounded bg-gray-900/50 hover:bg-cyan-500/20 text-white disabled:opacity-50 transition-colors"
                data-testid={`alt-swap-option-${exIndex}-${i}`}
              >
                → {a}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DataProgresionNew = ({ user, embedded = false }) => {
  const navigate = useNavigate();
  const { theme, getText, isMale } = useGender();
  const [stats, setStats] = useState({ total_workouts: 0, unique_exercises: 0, workouts_this_month: 0, current_streak: 0 });
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [exerciseProgress, setExerciseProgress] = useState({});
  const [saving, setSaving] = useState(false);
  const [workoutStartTime, setWorkoutStartTime] = useState(null);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [newRecords, setNewRecords] = useState([]);
  const [personalRecords, setPersonalRecords] = useState({});
  const [previousSets, setPreviousSets] = useState({});
  const [previousSetsNormalized, setPreviousSetsNormalized] = useState({});
  const [previousSetsByRoot, setPreviousSetsByRoot] = useState({});

  // Helper para buscar pesos previos con fallback inteligente cuando cambia el
  // nombre del ejercicio entre rutinas.
  // PRESERVA diferenciado: mancuerna/barra/polea/maquina/smith (cambian peso).
  // IGNORA: variaciones de postura/agarre/tempo (no afectan el peso).
  const findPreviousForExercise = useCallback((exerciseName) => {
    if (!exerciseName) return null;
    // 1) Match exacto
    if (previousSets[exerciseName]) return previousSets[exerciseName];

    const VARIATION_PATTERNS = [
      /\b(con\s+)?pies\s+(juntos|separados|abiertos|cerrados|elevados|en\s+(v|y))\b/g,
      /\b(con\s+)?(agarre|toma)\s+(supino|prono|neutro|mixto|ancho|cerrado|medio|estrecho)\b/g,
      /\bagarre\s+(supino|prono|neutro|mixto|ancho|cerrado|medio|estrecho)\b/g,
      /\b(palmas?|manos)\s+(hacia\s+(arriba|abajo|adentro|afuera|adelante|atras))\b/g,
      /\b(en|con)?\s*(pronacion|supinacion|martillo|hammer)\b/g,
      /\b(unilateral|bilateral|alternado|simetrico)\b/g,
      /\ba\s+(un|dos)\s+(brazo|brazos|pierna|piernas|mano|manos)\b/g,
      /\b(con|sin)\s+(pausa|pausado|explosivo|excentrico|isometrico|tempo)\b/g,
      /\b(tempo|pausa)\s+(controlad[oa]|lent[oa]|rapid[oa])\b/g,
    ];

    // Normalizar singular/plural de equipos para que "mancuerna" ≡ "mancuernas"
    const EQUIPMENT_NORMALIZE = [
      [/\bmancuernas\b/g, 'mancuerna'],
      [/\bbarras\b/g, 'barra'],
      [/\bpoleas\b/g, 'polea'],
      [/\bmaquinas\b/g, 'maquina'],
      [/\bkettlebells\b/g, 'kettlebell'],
      [/\bkbs?\b/g, 'kettlebell'],
    ];

    const normalize = (s) => {
      let n = String(s || '').toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      for (const pat of VARIATION_PATTERNS) {
        n = n.replace(pat, ' ');
      }
      for (const [pat, repl] of EQUIPMENT_NORMALIZE) {
        n = n.replace(pat, repl);
      }
      n = n.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
      return n;
    };

    const norm = normalize(exerciseName);
    if (norm && previousSetsNormalized[norm]) return previousSetsNormalized[norm];
    if (norm && previousSetsByRoot[norm]) return previousSetsByRoot[norm];
    return null;
  }, [previousSets, previousSetsNormalized, previousSetsByRoot]);
  const [restTimer, setRestTimer] = useState(null);
  const [restTimeLeft, setRestTimeLeft] = useState(0);
  const restIntervalRef = useRef(null);

  // Workout timer
  useEffect(() => {
    let interval = null;
    if (activeWorkout && workoutStartTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((new Date() - workoutStartTime) / 1000));
      }, 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [activeWorkout, workoutStartTime]);

  // Rest timer
  useEffect(() => {
    if (restTimer && restTimeLeft > 0) {
      restIntervalRef.current = setInterval(() => {
        setRestTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(restIntervalRef.current);
            setRestTimer(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (restIntervalRef.current) clearInterval(restIntervalRef.current); };
  }, [restTimer]);

  const startRestTimer = useCallback((seconds) => {
    if (restIntervalRef.current) clearInterval(restIntervalRef.current);
    setRestTimeLeft(parseInt(seconds) || 60);
    setRestTimer(Date.now());
  }, []);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const savedUser = localStorage.getItem('deisycfit_user');
      let userEmail = null;
      if (savedUser) { userEmail = JSON.parse(savedUser).email; }
      
      const [statsRes, workoutsRes, recordsRes, prevRes] = await Promise.all([
        axios.get(`${API}/workouts/stats/summary`, { headers }),
        axios.get(`${API}/workouts?limit=10`, { headers }),
        axios.get(`${API}/workouts/personal-records`, { headers }).catch(() => ({ data: { records: [] } })),
        axios.get(`${API}/workouts/previous-sets`, { headers }).catch(() => ({ data: { previous: {} } }))
      ]);
      
      setStats(statsRes.data);
      setRecentWorkouts(workoutsRes.data);
      setPreviousSets(prevRes.data.previous || {});
      setPreviousSetsNormalized(prevRes.data.previous_normalized || {});
      setPreviousSetsByRoot(prevRes.data.previous_by_root || {});
      
      const recordsMap = {};
      (recordsRes.data.records || []).forEach(r => { recordsMap[r.exercise_name] = r; });
      setPersonalRecords(recordsMap);
      
      try {
        const planRes = await axios.get(`${API}/user/workout-plan`, { headers });
        if (planRes.data && (planRes.data.days || planRes.data.training_days)) {
          setWorkoutPlan(planRes.data);
        }
      } catch (planError) {
        if (userEmail) {
          try {
            const planByEmail = await axios.get(`${API}/workout/active/by-email/${encodeURIComponent(userEmail)}`);
            if (planByEmail.data && (planByEmail.data.days || planByEmail.data.training_days)) {
              setWorkoutPlan(planByEmail.data);
            }
          } catch (e) { /* no plan */ }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse series_detail strings like ["4x8", "Drop sets triples"] → {numSets, reps, method}
  const parseSeriesDetail = (seriesDetail) => {
    if (!seriesDetail || !Array.isArray(seriesDetail) || seriesDetail.length === 0) return [];
    
    const sets = [];
    let method = 'normal';
    let methodNotes = '';
    
    for (const item of seriesDetail) {
      if (!item || typeof item !== 'string') continue;
      const trimmed = item.trim();
      
      // Check if it's a set x rep pattern like "4x8", "3x12", "1x15"
      const sxrMatch = trimmed.match(/^(\d+)\s*[xX×]\s*(\d+)$/);
      if (sxrMatch) {
        const count = parseInt(sxrMatch[1]);
        const reps = parseInt(sxrMatch[2]);
        for (let i = 0; i < count; i++) {
          sets.push({ reps: String(reps), method, methodNotes: '' });
        }
        continue;
      }
      
      // Check for method indicators
      const lower = trimmed.toLowerCase();
      if (lower.includes('drop set') || lower.includes('dropset') || lower.includes('drop sets')) {
        method = 'dropset';
        methodNotes = trimmed;
      } else if (lower.includes('rest-pause') || lower.includes('rest pause')) {
        method = 'rest_pause';
        methodNotes = trimmed;
      } else if (lower.includes('myo-rep') || lower.includes('myo rep')) {
        method = 'myo_reps';
        methodNotes = trimmed;
      } else if (lower.includes('cluster')) {
        method = 'cluster';
        methodNotes = trimmed;
      } else if (lower.startsWith('tempo')) {
        method = 'tempo_controlado';
        methodNotes = trimmed;
      } else if (lower.includes('isométric') || lower.includes('isometric')) {
        method = 'pausas_isometricas';
        methodNotes = trimmed;
      } else if (lower.includes('excéntrica') || lower.includes('excentrica')) {
        method = 'excentrica_lenta';
        methodNotes = trimmed;
      } else if (lower.includes('super') && lower.includes('serie')) {
        method = 'super_serie';
        methodNotes = trimmed;
      } else if (lower.includes('amrap')) {
        method = 'amrap';
        methodNotes = trimmed;
      } else if (lower.includes('emom')) {
        method = 'emom';
        methodNotes = trimmed;
      } else if (lower.includes('fst')) {
        method = 'fst-7';
        methodNotes = trimmed;
      } else if (lower.includes('back') && lower.includes('off')) {
        method = 'back_off_set';
        methodNotes = trimmed;
      } else {
        // Unknown pattern - treat as method notes
        methodNotes = trimmed;
      }
    }
    
    // Apply method to all sets that were parsed
    if (method !== 'normal') {
      sets.forEach(s => { s.method = method; s.methodNotes = methodNotes; });
    }
    
    return sets;
  };

  const startWorkout = (day) => {
    setSelectedDay(day);
    setActiveWorkout(day);
    setWorkoutStartTime(new Date());
    
    const initialProgress = {};
    day.exercises.forEach((exercise, exIndex) => {
      const hasSeries = exercise.series && Array.isArray(exercise.series) && exercise.series.length > 0;
      let parsedSets = [];
      
      if (hasSeries) {
        // Format from OfflineGenerator with structured series objects
        parsedSets = exercise.series.map((s) => ({
          reps: s.reps?.toString() || '',
          method: s.method || 'normal',
          rest: s.rest || '60',
          methodNotes: s.custom_method_notes || '',
          tempo_eccentric: s.tempo_eccentric,
          tempo_pause: s.tempo_pause,
          tempo_concentric: s.tempo_concentric,
          isometric_seconds: s.isometric_seconds,
          eccentric_seconds: s.eccentric_seconds,
        }));
      } else if (exercise.series_detail && Array.isArray(exercise.series_detail)) {
        // Detectar si series_detail viene como ARRAY DE OBJETOS estructurados
        // (formato producido por `build_workout_days` — Fit Evolution) O como
        // ARRAY DE STRINGS (formato AI clásico "3x8-10").
        const firstItem = exercise.series_detail[0];
        const isStructured = firstItem && typeof firstItem === 'object' && (
          'method' in firstItem || 'set' in firstItem || 'method_name' in firstItem
        );

        if (isStructured) {
          // ✅ Fit Evolution: cada objeto = una serie con su técnica.
          // Preserva method / method_name / method_description por serie
          // (permite renderizar el chip Rest-Pause/Drop set/Tempo… en cada fila).
          parsedSets = exercise.series_detail.map((s) => ({
            reps: (s.reps ?? '').toString(),
            method: s.method || 'normal',
            rest: (s.rest ?? '60').toString(),
            methodNotes: s.method_description || s.method_name || s.notes || '',
            tempo_eccentric: s.tempo_eccentric,
            tempo_pause: s.tempo_pause,
            tempo_concentric: s.tempo_concentric,
            isometric_seconds: s.isometric_seconds,
            eccentric_seconds: s.eccentric_seconds,
          }));
        } else {
          // Formato clásico de strings: parsear "4x8", "Drop set triple", etc.
          parsedSets = parseSeriesDetail(exercise.series_detail);
        }
      }
      
      // Fallback: create 4 default sets if nothing was parsed
      if (parsedSets.length === 0) {
        const defaultReps = exercise.reps || '12';
        parsedSets = Array(parseInt(exercise.sets) || 4).fill(null).map(() => ({
          reps: defaultReps.toString(), method: 'normal', rest: '60', methodNotes: ''
        }));
      }

      // ✅ FIX Fit Evolution: si TODAS las series quedaron como 'normal' pero
      // el ejercicio tiene `execution` con una técnica reconocible (ej. "Rest-Pause",
      // "Drop set", "Tempo controlado"…), aplicar esa técnica a todas las series
      // para que aparezca el chip por serie como en el plan Premium.
      const allNormal = parsedSets.every((s) => (s.method || 'normal') === 'normal');
      const execRaw = (exercise.execution || exercise.notes || exercise.method_notes || '').toString();
      if (allNormal && execRaw) {
        const execLower = execRaw.toLowerCase();
        let inferredMethod = null;
        if (execLower.includes('drop set') || execLower.includes('dropset')) inferredMethod = 'dropset';
        else if (execLower.includes('rest-pause') || execLower.includes('rest pause')) inferredMethod = 'rest_pause';
        else if (execLower.includes('myo-rep') || execLower.includes('myo rep')) inferredMethod = 'myo_reps';
        else if (execLower.includes('cluster')) inferredMethod = 'cluster';
        else if (execLower.startsWith('tempo') || execLower.includes(' tempo ')) inferredMethod = 'tempo_controlado';
        else if (execLower.includes('isométric') || execLower.includes('isometric')) inferredMethod = 'pausas_isometricas';
        else if (execLower.includes('excéntric') || execLower.includes('excentric')) inferredMethod = 'excentrica_lenta';
        else if (execLower.includes('super') && execLower.includes('serie')) inferredMethod = 'super_serie';
        else if (execLower.includes('back') && execLower.includes('off')) inferredMethod = 'back_off_set';
        else if (execLower.includes('amrap')) inferredMethod = 'amrap';
        if (inferredMethod) {
          parsedSets = parsedSets.map((s) => ({ ...s, method: inferredMethod, methodNotes: execRaw }));
        }
      }
      
      initialProgress[exIndex] = {
        completed: false,
        sets: parsedSets.map((s, i) => {
          const setMethod = s.method || 'normal';
          const subConfig = getSubSetConfig(setMethod, s.methodNotes);
          
          return {
            set_number: i + 1,
            reps: '',
            weight: '',
            completed: false,
            is_warmup: false,
            notes: '',
            target_reps: s.reps,
            rest: s.rest || '60',
            method: setMethod,
            custom_method_notes: s.methodNotes || '',
            tempo_eccentric: s.tempo_eccentric,
            tempo_pause: s.tempo_pause,
            tempo_concentric: s.tempo_concentric,
            isometric_seconds: s.isometric_seconds,
            eccentric_seconds: s.eccentric_seconds,
            // Sub-series para técnicas avanzadas (dropset, rest-pause, myo-reps, cluster)
            drops: subConfig ? Array.from({ length: subConfig.count }, (_, d) => ({
              weight: '', weight_lbs: '', reps: '', label: subConfig.labels[d]
            })) : null,
            subConfig: subConfig || null,
          };
        })
      };
    });
    setExerciseProgress(initialProgress);
  };

  // Conversión KG <-> LBS
  const KG_TO_LBS = 2.20462;
  const kgToLbs = (kg) => kg ? (parseFloat(kg) * KG_TO_LBS).toFixed(1) : '';
  const lbsToKg = (lbs) => lbs ? (parseFloat(lbs) / KG_TO_LBS).toFixed(1) : '';

  const updateSetProgress = (exIndex, setIndex, field, value) => {
    setExerciseProgress(prev => ({
      ...prev,
      [exIndex]: {
        ...prev[exIndex],
        sets: prev[exIndex].sets.map((set, i) => {
          if (i !== setIndex) return set;
          if (field === 'weight') {
            return { ...set, weight: value, weight_lbs: value ? kgToLbs(value) : '' };
          }
          if (field === 'weight_lbs') {
            return { ...set, weight: value ? lbsToKg(value) : '', weight_lbs: value };
          }
          return { ...set, [field]: value };
        })
      }
    }));
  };

  // Actualizar un drop específico dentro de un set
  const updateDropProgress = (exIndex, setIndex, dropIndex, field, value) => {
    setExerciseProgress(prev => ({
      ...prev,
      [exIndex]: {
        ...prev[exIndex],
        sets: prev[exIndex].sets.map((set, i) => {
          if (i !== setIndex || !set.drops) return set;
          const newDrops = set.drops.map((drop, d) => {
            if (d !== dropIndex) return drop;
            if (field === 'weight') {
              return { ...drop, weight: value, weight_lbs: value ? kgToLbs(value) : '' };
            }
            if (field === 'weight_lbs') {
              return { ...drop, weight: value ? lbsToKg(value) : '', weight_lbs: value };
            }
            return { ...drop, [field]: value };
          });
          return { ...set, drops: newDrops };
        })
      }
    }));
  };

  const toggleSetComplete = (exIndex, setIndex) => {
    setExerciseProgress(prev => {
      const set = prev[exIndex].sets[setIndex];
      const newCompleted = !set.completed;
      
      // Start rest timer if completing a set
      if (newCompleted && set.rest) {
        const restSecs = set.rest === 'custom' ? 60 : parseInt(set.rest) || 60;
        startRestTimer(restSecs);
      }
      
      return {
        ...prev,
        [exIndex]: {
          ...prev[exIndex],
          sets: prev[exIndex].sets.map((s, i) => 
            i === setIndex ? { ...s, completed: newCompleted } : s
          )
        }
      };
    });
  };

  const saveWorkout = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const exercises = selectedDay.exercises.map((exercise, exIndex) => ({
        exercise_name: exercise.exercise_name || exercise.name || 'Ejercicio',
        exercise_category: exercise.muscle_group || exercise.primary_muscle || exercise.exercise_category || 'general',
        sets: exerciseProgress[exIndex]?.sets.filter(s => s.completed || (embedded && ((parseFloat(s.weight) || 0) > 0 || (parseInt(s.reps) || 0) > 0 || (s.drops || []).some(d => (parseInt(d.reps) || 0) > 0)))).map(s => {
          // Para técnicas con sub-series (dropset, rest-pause, myo-reps, cluster)
          if (s.drops && s.drops.length > 0) {
            const isWeightPerSub = s.subConfig?.weightPerSub !== false;
            const methodName = s.method || 'technique';
            return {
              set_number: s.set_number,
              reps: s.drops.reduce((sum, d) => sum + (parseInt(d.reps) || 0), 0),
              weight: isWeightPerSub ? (parseFloat(s.drops[0]?.weight) || 0) : (parseFloat(s.weight) || 0),
              is_warmup: s.is_warmup,
              notes: isWeightPerSub 
                ? `${methodName}: ${s.drops.map(d => `${d.weight || 0}kg×${d.reps || 0}`).join(' → ')}`
                : `${methodName}: ${parseFloat(s.weight) || 0}kg — ${s.drops.map(d => `${d.reps || 0} reps`).join(' + ')}`,
              drops: s.drops.map(d => ({
                weight: isWeightPerSub ? (parseFloat(d.weight) || 0) : (parseFloat(s.weight) || 0),
                reps: parseInt(d.reps) || 0
              }))
            };
          }
          return {
            set_number: s.set_number,
            reps: parseInt(s.reps) || 0,
            weight: parseFloat(s.weight) || 0,
            is_warmup: s.is_warmup,
            notes: s.notes
          };
        }) || []
      })).filter(e => e.sets.length > 0);

      if (exercises.length === 0) {
        toast.error(getText('No hay series con peso o repeticiones para guardar. Escribe los datos (o marca ✓) antes de finalizar.', 'No hay series con peso o repeticiones para guardar. Escribe los datos (o marca ✓) antes de finalizar.'));
        setSaving(false);
        return;
      }

      const duration = workoutStartTime ? Math.round((new Date() - workoutStartTime) / 60000) : 0;
      const response = await axios.post(`${API}/workouts`, {
        workout_name: selectedDay.day_name,
        workout_date: new Date().toISOString().split('T')[0],
        duration_minutes: duration,
        exercises,
        notes: `Rutina completada: ${selectedDay.focus || selectedDay.day_name}`
      }, { headers });
      
      if (response.data.has_new_records && response.data.new_records?.length > 0) {
        setNewRecords(response.data.new_records);
        setShowRecordsModal(true);
      } else {
        toast.success(getText('¡Entreno guardado, Guerrero!', '¡Entreno guardado, Guerrera!'));
      }
      
      setActiveWorkout(null);
      setSelectedDay(null);
      setExerciseProgress({});
      setElapsedTime(0);
      setRestTimer(null);
      fetchData();
    } catch (error) {
      console.error('Error saving workout:', error);
      toast.error(error?.response?.data?.detail || 'Error al guardar el entreno');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Check if current weight/reps beats PR
  const isPRBeat = (exerciseName, weight, reps) => {
    const pr = personalRecords[exerciseName];
    if (!pr) return { weight: weight > 0, reps: reps > 0 };
    return {
      weight: parseFloat(weight) > (pr.max_weight || 0) && parseFloat(weight) > 0,
      reps: parseInt(reps) > (pr.max_reps || 0) && parseInt(reps) > 0
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full animate-pulse mx-auto mb-4" style={{ background: `linear-gradient(135deg, ${theme.primary}, #00F0FF)` }} />
          <p className="text-gray-400 text-lg">Cargando tu rutina...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // ACTIVE WORKOUT VIEW - Hevy-inspired
  // ============================================================
  if (activeWorkout) {
    const totalCompletedSets = Object.values(exerciseProgress).reduce(
      (acc, ex) => acc + (ex.sets?.filter(s => s.completed).length || 0), 0
    );
    const totalSets = Object.values(exerciseProgress).reduce(
      (acc, ex) => acc + (ex.sets?.length || 0), 0
    );

    return (
      <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }} data-testid="active-workout-view">
        {/* Sticky Header */}
        <div className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ backgroundColor: 'rgba(10,10,10,0.95)', borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" size="sm" data-testid="cancel-workout-btn"
                  className="text-gray-400 hover:text-white hover:bg-white/10 h-9 w-9 p-0"
                  onClick={() => {
                    if (window.confirm('¿Seguro que quieres salir? Se perderá el progreso.')) {
                      setActiveWorkout(null); setSelectedDay(null); setElapsedTime(0); setRestTimer(null);
                    }
                  }}
                >
                  <FaTimes />
                </Button>
                <div>
                  <h1 className="text-base font-bold text-white">{activeWorkout.day_name}</h1>
                  <p className="text-xs text-gray-400">{totalCompletedSets}/{totalSets} series</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                  <FaStopwatch className="text-sm" style={{ color: theme.primary }} />
                  <span className="text-lg font-mono font-bold text-white">{formatTime(elapsedTime)}</span>
                </div>
                <Button 
                  onClick={saveWorkout} disabled={saving} data-testid="finish-workout-btn"
                  className="h-9 px-4 text-sm font-bold text-white rounded-lg"
                  style={{ backgroundColor: theme.primary }}
                >
                  {saving ? '...' : 'Finalizar'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Rest Timer Banner */}
        {((b) => (embedded ? createPortal(b, document.body) : b))(<AnimatePresence>
          {restTimer && restTimeLeft > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className={embedded ? 'fixed bottom-4 inset-x-0 z-[90] mx-auto w-[92%] max-w-xl rounded-xl border shadow-2xl backdrop-blur-xl' : 'sticky top-[60px] z-40 border-b'}
              style={{ backgroundColor: embedded ? 'rgba(10, 40, 22, 0.95)' : 'rgba(34, 197, 94, 0.15)', borderColor: 'rgba(34, 197, 94, 0.3)' }}
              data-testid="rest-timer-banner"
            >
              <div className="max-w-2xl mx-auto px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.3)' }}>
                    <FaClock className="text-green-400 text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-400">Descanso</p>
                    <p className="text-2xl font-mono font-bold text-white">{formatTime(restTimeLeft)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white text-xs"
                    onClick={() => setRestTimeLeft(prev => prev + 30)}>+30s</Button>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 text-xs"
                    onClick={() => { clearInterval(restIntervalRef.current); setRestTimer(null); setRestTimeLeft(0); }}>Saltar</Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>)}

        {/* Exercise List */}
        <div className="max-w-2xl mx-auto px-4 py-4 space-y-4 pb-24">
          {(!activeWorkout.exercises || activeWorkout.exercises.length === 0) ? (
            <Card className="border-2 border-dashed" style={{ borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent className="py-12 text-center">
                <FaDumbbell className="text-5xl mx-auto mb-4" style={{ color: `${theme.primary}50` }} />
                <h3 className="text-lg font-bold text-white mb-2">Este día no tiene ejercicios asignados</h3>
                <Button variant="outline" onClick={() => { setActiveWorkout(null); setSelectedDay(null); }}
                  style={{ borderColor: theme.primary, color: theme.primary }}>
                  <FaArrowLeft className="mr-2" /> Volver
                </Button>
              </CardContent>
            </Card>
          ) : activeWorkout.exercises.map((exercise, exIndex) => {
            const progress = exerciseProgress[exIndex];
            const completedSets = progress?.sets.filter(s => s.completed).length || 0;
            const totalExSets = progress?.sets?.length || 0;
            const exerciseName = exercise.custom_name || exercise.exercise_name || exercise.name || 'Ejercicio';
            const exercisePR = personalRecords[exerciseName];
            const prevData = findPreviousForExercise(exerciseName);
            const allComplete = completedSets === totalExSets;
            
            return (
              <motion.div
                key={exIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: exIndex * 0.05 }}
                data-testid={`exercise-card-${exIndex}`}
              >
                <div className="rounded-xl overflow-hidden" style={{ 
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: allComplete ? `2px solid ${theme.primary}` : '1px solid rgba(255,255,255,0.08)'
                }}>
                  {/* Exercise Header */}
                  <div className="px-4 py-3" style={{ backgroundColor: allComplete ? `${theme.primary}10` : 'rgba(255,255,255,0.02)' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm"
                          style={{ backgroundColor: allComplete ? `${theme.primary}30` : 'rgba(255,255,255,0.08)', color: allComplete ? theme.primary : '#9CA3AF' }}>
                          {allComplete ? <FaCheck /> : `${exIndex + 1}`}
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-base font-bold text-white truncate">{exerciseName}</h3>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-xs text-gray-500">{exercise.muscle_group || ''}</span>
                            {exercisePR && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium" data-testid={`pr-badge-${exIndex}`}>
                                PR: {exercisePR.max_weight}kg
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <span className="text-lg font-bold" style={{ color: allComplete ? theme.primary : '#6B7280' }}>
                          {completedSets}/{totalExSets}
                        </span>
                      </div>
                    </div>
                    {exercise.notes && (
                      <p className="mt-2 text-xs text-gray-500 italic pl-[52px]">{exercise.notes}</p>
                    )}
                    {/* 2026-02 · Alternativas asignadas por el admin.
                        Si existen, el usuario puede abrir un selector y
                        reemplazar permanentemente el ejercicio. */}
                    {Array.isArray(exercise.alternatives) && exercise.alternatives.length > 0 && (
                      <AlternativeExerciseSwap
                        exercise={exercise}
                        exIndex={exIndex}
                        dayIndex={activeWorkout.day_index}
                        onSwapped={(newName) => {
                          setActiveWorkout(prev => {
                            if (!prev) return prev;
                            const exs = [...(prev.exercises || [])];
                            if (exs[exIndex]) {
                              exs[exIndex] = { ...exs[exIndex], name: newName, exercise_name: newName, custom_name: newName };
                            }
                            return { ...prev, exercises: exs };
                          });
                        }}
                      />
                    )}
                  </div>
                  
                  {/* Set Table - Hevy Style */}
                  <div className="px-4 pb-4">
                    {/* Table Header */}
                    <div className="grid grid-cols-[32px_1fr_1fr_1fr_1fr_40px] gap-1 py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                      <div className="text-center">
                        <span className="text-xs font-semibold text-gray-500">SET</span>
                      </div>
                      <div className="text-center">
                        <span className="text-xs font-semibold text-gray-500">ANTERIOR</span>
                      </div>
                      <div className="text-center">
                        <span className="text-xs font-semibold text-gray-500">KG</span>
                      </div>
                      <div className="text-center">
                        <span className="text-xs font-semibold text-gray-500">LBS</span>
                      </div>
                      <div className="text-center">
                        <span className="text-xs font-semibold text-gray-500">REPS</span>
                      </div>
                      <div className="text-center">
                        <span className="text-xs font-semibold text-gray-500"></span>
                      </div>
                    </div>
                    
                    {/* Set Rows */}
                    {progress?.sets.map((set, setIndex) => {
                      const setMethod = set.method || 'normal';
                      const methodInfo = getMethodInfo(setMethod);
                      const isSpecialMethod = setMethod && setMethod !== 'normal';
                      const prevSet = prevData?.sets?.[setIndex];
                      const prCheck = isPRBeat(exerciseName, set.weight, set.reps);
                      
                      // Build method label
                      let methodLabel = methodInfo.name;
                      if (setMethod === 'tempo_controlado' && set.tempo_eccentric) {
                        methodLabel = `Tempo ${set.tempo_eccentric}-${set.tempo_pause || 1}-${set.tempo_concentric || 1}`;
                      } else if (setMethod === 'pausas_isometricas' && set.isometric_seconds) {
                        methodLabel = `Iso ${set.isometric_seconds}s`;
                      } else if (setMethod === 'excentrica_lenta' && set.eccentric_seconds) {
                        methodLabel = `Exc ${set.eccentric_seconds}s`;
                      }
                      
                      return (
                        <div key={setIndex} data-testid={`set-row-${exIndex}-${setIndex}`}>
                          {/* Method badge row (if special) */}
                          {isSpecialMethod && (setIndex === 0 || set.method !== progress.sets[setIndex - 1]?.method) && (
                            <div className="py-1.5 mt-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" 
                                  style={{ backgroundColor: `${methodInfo.color}20`, color: methodInfo.color, border: `1px solid ${methodInfo.color}40` }}>
                                  {methodInfo.icon} {methodLabel}
                                </span>
                                {set.rest && (
                                  <span className="text-xs text-gray-600">
                                    Descanso: {REST_LABELS[set.rest] || `${set.rest}s`}
                                  </span>
                                )}
                              </div>
                              {/* Method description */}
                              {(set.custom_method_notes || methodInfo.description) && (
                                <div className="mt-1.5 p-2.5 rounded-lg text-xs leading-relaxed whitespace-pre-line"
                                  style={{ 
                                    backgroundColor: `${methodInfo.color}08`, 
                                    borderLeft: `3px solid ${methodInfo.color}60`,
                                    color: '#9CA3AF'
                                  }}>
                                  {set.custom_method_notes || methodInfo.description}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* ADVANCED TECHNIQUE layout: sub-series (drops/minis) */}
                          {set.drops && set.drops.length > 0 ? (
                            <div className={`rounded-lg my-1 transition-all ${set.completed ? 'bg-green-500/10' : ''}`}
                              style={{ borderLeft: `3px solid ${methodInfo.color}60` }}>
                              {/* Set header with number, ANTERIOR summary and complete button */}
                              <div className="flex items-center justify-between px-3 pt-2 pb-1 gap-2">
                                <span className={`text-sm font-bold ${set.completed ? 'text-green-400' : 'text-gray-400'}`}>
                                  Serie {setIndex + 1}
                                </span>
                                {/* Anterior — resumen de la última vez para esta serie */}
                                {(() => {
                                  // Construir label "Anterior" para la serie completa (drops + peso principal)
                                  let prevLabel = null;
                                  if (prevSet?.drops && prevSet.drops.length > 0) {
                                    const parts = prevSet.drops
                                      .filter(d => (d?.weight ?? 0) > 0 || (d?.reps ?? 0) > 0)
                                      .map(d => `${d.weight || 0}kg×${d.reps || 0}`);
                                    if (parts.length > 0) prevLabel = parts.join(' / ');
                                  }
                                  if (!prevLabel && prevSet && ((prevSet.weight ?? 0) > 0 || (prevSet.reps ?? 0) > 0)) {
                                    prevLabel = `${prevSet.weight || 0}kg×${prevSet.reps || 0}`;
                                  }
                                  return prevLabel ? (
                                    <span className="text-[10px] text-gray-500 font-mono truncate flex-1 text-right mr-2" data-testid={`set-previous-summary-${exIndex}-${setIndex}`}>
                                      Anterior: {prevLabel}
                                    </span>
                                  ) : null;
                                })()}
                                <button
                                  onClick={() => toggleSetComplete(exIndex, setIndex)}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all flex-shrink-0 ${
                                    set.completed 
                                      ? 'text-white shadow-md' 
                                      : 'border-2 border-gray-600 text-gray-600 hover:border-gray-400'
                                  }`}
                                  style={{ backgroundColor: set.completed ? '#22C55E' : 'transparent' }}
                                  data-testid={`complete-set-btn-${exIndex}-${setIndex}`}
                                >
                                  <FaCheck className={set.completed ? 'text-sm' : 'text-xs'} />
                                </button>
                              </div>

                              {/* weightPerSub=false: mismo peso, múltiples reps (rest-pause, myo-reps, cluster) */}
                              {set.subConfig && !set.subConfig.weightPerSub && (
                                <>
                                  {/* Single weight row */}
                                  <div className="grid grid-cols-[60px_1fr_1fr] gap-1 items-center px-3 py-1">
                                    <span className="text-[10px] text-gray-500 text-center font-semibold">PESO</span>
                                    <input
                                      type="number" inputMode="decimal" placeholder="KG"
                                      value={set.weight || ''}
                                      onChange={(e) => updateSetProgress(exIndex, setIndex, 'weight', e.target.value)}
                                      className="w-full h-8 text-center text-sm font-bold rounded-lg border-0 outline-none"
                                      style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)', color: set.completed ? '#86efac' : 'white' }}
                                      data-testid={`weight-input-${exIndex}-${setIndex}`}
                                    />
                                    <input
                                      type="number" inputMode="decimal" placeholder="LBS"
                                      value={set.weight_lbs || ''}
                                      onChange={(e) => updateSetProgress(exIndex, setIndex, 'weight_lbs', e.target.value)}
                                      className="w-full h-8 text-center text-sm font-bold rounded-lg border-0 outline-none"
                                      style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)', color: set.completed ? '#86efac' : 'white' }}
                                      data-testid={`weight-lbs-input-${exIndex}-${setIndex}`}
                                    />
                                  </div>
                                  <div className="grid grid-cols-[60px_1fr_1fr] gap-1 px-3 pb-0.5">
                                    <div />
                                    <span className="text-[10px] text-gray-600 text-center">KG</span>
                                    <span className="text-[10px] text-gray-600 text-center">LBS</span>
                                  </div>
                                  {/* Multiple reps rows */}
                                  {set.drops.map((drop, dropIdx) => (
                                    <div key={dropIdx} className="grid grid-cols-[60px_1fr] gap-1 items-center px-3 py-1"
                                      data-testid={`drop-row-${exIndex}-${setIndex}-${dropIdx}`}>
                                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-center"
                                        style={{ backgroundColor: `${methodInfo.color}15`, color: methodInfo.color }}>
                                        {drop.label}
                                      </span>
                                      <input
                                        type="number" inputMode="numeric"
                                        placeholder={set.target_reps?.toString() || 'Reps'}
                                        value={drop.reps}
                                        onChange={(e) => updateDropProgress(exIndex, setIndex, dropIdx, 'reps', e.target.value)}
                                        className="w-full h-8 text-center text-sm font-bold rounded-lg border-0 outline-none"
                                        style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)', color: set.completed ? '#86efac' : 'white' }}
                                        data-testid={`drop-reps-${exIndex}-${setIndex}-${dropIdx}`}
                                      />
                                    </div>
                                  ))}
                                  <div className="grid grid-cols-[60px_1fr] gap-1 px-3 pb-1.5">
                                    <div />
                                    <span className="text-[10px] text-gray-600 text-center">REPS</span>
                                  </div>
                                </>
                              )}

                              {/* weightPerSub=true: peso diferente cada sub-serie (drop sets) */}
                              {(!set.subConfig || set.subConfig.weightPerSub) && (
                                <>
                                  {set.drops.map((drop, dropIdx) => {
                                    const prevDrop = prevSet?.drops?.[dropIdx];
                                    const prevDropLabel = prevDrop && (prevDrop.weight > 0 || prevDrop.reps > 0)
                                      ? `${prevDrop.weight || 0}kg × ${prevDrop.reps || 0}`
                                      : null;
                                    return (
                                    <div key={dropIdx} data-testid={`drop-row-${exIndex}-${setIndex}-${dropIdx}`}>
                                      <div className="grid grid-cols-[60px_1fr_1fr_1fr] gap-1 items-center px-3 py-1">
                                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-center"
                                          style={{ backgroundColor: `${methodInfo.color}15`, color: methodInfo.color }}>
                                          {drop.label}
                                        </span>
                                        <input
                                          type="number" inputMode="decimal" placeholder="KG"
                                          value={drop.weight}
                                          onChange={(e) => updateDropProgress(exIndex, setIndex, dropIdx, 'weight', e.target.value)}
                                          className="w-full h-8 text-center text-sm font-bold rounded-lg border-0 outline-none"
                                          style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)', color: set.completed ? '#86efac' : 'white' }}
                                          data-testid={`drop-kg-${exIndex}-${setIndex}-${dropIdx}`}
                                        />
                                        <input
                                          type="number" inputMode="decimal" placeholder="LBS"
                                          value={drop.weight_lbs || ''}
                                          onChange={(e) => updateDropProgress(exIndex, setIndex, dropIdx, 'weight_lbs', e.target.value)}
                                          className="w-full h-8 text-center text-sm font-bold rounded-lg border-0 outline-none"
                                          style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)', color: set.completed ? '#86efac' : 'white' }}
                                          data-testid={`drop-lbs-${exIndex}-${setIndex}-${dropIdx}`}
                                        />
                                        <input
                                          type="number" inputMode="numeric"
                                          placeholder={set.target_reps?.toString() || '0'}
                                          value={drop.reps}
                                          onChange={(e) => updateDropProgress(exIndex, setIndex, dropIdx, 'reps', e.target.value)}
                                          className="w-full h-8 text-center text-sm font-bold rounded-lg border-0 outline-none"
                                          style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)', color: set.completed ? '#86efac' : 'white' }}
                                          data-testid={`drop-reps-${exIndex}-${setIndex}-${dropIdx}`}
                                        />
                                      </div>
                                      {prevDropLabel && (
                                        <div className="px-3 pb-1 text-[10px] text-gray-500 font-mono" data-testid={`drop-previous-${exIndex}-${setIndex}-${dropIdx}`}>
                                          Anterior: {prevDropLabel}
                                        </div>
                                      )}
                                    </div>
                                    );
                                  })}
                                  {setIndex === 0 && (
                                    <div className="grid grid-cols-[60px_1fr_1fr_1fr] gap-1 px-3 pb-1.5">
                                      <div />
                                      <span className="text-[10px] text-gray-600 text-center">KG</span>
                                      <span className="text-[10px] text-gray-600 text-center">LBS</span>
                                      <span className="text-[10px] text-gray-600 text-center">REPS</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ) : (
                          /* NORMAL set data row */
                          <div className={`grid grid-cols-[32px_1fr_1fr_1fr_1fr_40px] gap-1 items-center py-1.5 rounded-lg my-0.5 transition-all ${
                            set.completed ? 'bg-green-500/10' : ''
                          }`}>
                            {/* Set Number */}
                            <div className="text-center">
                              <span className={`text-sm font-bold ${set.completed ? 'text-green-400' : 'text-gray-400'}`}>
                                {setIndex + 1}
                              </span>
                            </div>
                            
                            {/* Previous */}
                            <div className="text-center">
                              {prevSet ? (
                                <span className="text-xs text-gray-500 font-mono">
                                  {prevSet.weight > 0 ? `${prevSet.weight}kg×${prevSet.reps}` : `${prevSet.reps} reps`}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-700">-</span>
                              )}
                            </div>
                            
                            {/* KG Input */}
                            <div className="px-0.5">
                              <div className="relative">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  placeholder="0"
                                  value={set.weight}
                                  onChange={(e) => updateSetProgress(exIndex, setIndex, 'weight', e.target.value)}
                                  className={`w-full h-9 text-center text-sm font-bold rounded-lg border-0 outline-none transition-all ${
                                    set.completed ? 'bg-green-500/15 text-green-300' : 'bg-white/8 text-white'
                                  }`}
                                  style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)' }}
                                  data-testid={`weight-input-${exIndex}-${setIndex}`}
                                />
                                {set.completed && prCheck.weight && (
                                  <span className="absolute -top-1 -right-1 text-xs bg-amber-500 text-black rounded-full w-4 h-4 flex items-center justify-center font-bold">!</span>
                                )}
                              </div>
                            </div>

                            {/* LBS Input */}
                            <div className="px-0.5">
                              <input
                                type="number"
                                inputMode="decimal"
                                placeholder="0"
                                value={set.weight_lbs || ''}
                                onChange={(e) => updateSetProgress(exIndex, setIndex, 'weight_lbs', e.target.value)}
                                className={`w-full h-9 text-center text-sm font-bold rounded-lg border-0 outline-none transition-all ${
                                  set.completed ? 'bg-green-500/15 text-green-300' : 'bg-white/8 text-white'
                                }`}
                                style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)' }}
                                data-testid={`weight-lbs-input-${exIndex}-${setIndex}`}
                              />
                            </div>
                            
                            {/* Reps Input */}
                            <div className="px-0.5">
                              <input
                                type="number"
                                inputMode="numeric"
                                placeholder={set.target_reps?.toString() || '0'}
                                value={set.reps}
                                onChange={(e) => updateSetProgress(exIndex, setIndex, 'reps', e.target.value)}
                                className={`w-full h-9 text-center text-sm font-bold rounded-lg border-0 outline-none transition-all ${
                                  set.completed ? 'bg-green-500/15 text-green-300' : 'bg-white/8 text-white'
                                }`}
                                style={{ backgroundColor: set.completed ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)' }}
                                data-testid={`reps-input-${exIndex}-${setIndex}`}
                              />
                            </div>
                            
                            {/* Complete Checkbox */}
                            <div className="flex justify-center">
                              <button
                                onClick={() => toggleSetComplete(exIndex, setIndex)}
                                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                                  set.completed 
                                    ? 'text-white shadow-md' 
                                    : 'border-2 border-gray-600 text-gray-600 hover:border-gray-400'
                                }`}
                                style={{ backgroundColor: set.completed ? '#22C55E' : 'transparent' }}
                                data-testid={`complete-set-btn-${exIndex}-${setIndex}`}
                              >
                                <FaCheck className={set.completed ? 'text-sm' : 'text-xs'} />
                              </button>
                            </div>
                          </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Rest info for normal sets */}
                    {progress?.sets?.[0]?.method === 'normal' && progress?.sets?.[0]?.rest && (
                      <div className="text-center pt-2 border-t mt-2" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        <span className="text-xs text-gray-600">
                          Descanso: {REST_LABELS[progress.sets[0].rest] || `${progress.sets[0].rest}s`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
          
          {/* Finish Button */}
          {/* 2026-02 · Antes era `fixed bottom-0 left-0 right-0` y tapaba el
              último ejercicio bloqueando el scroll. Ahora queda al final de
              la lista como un bloque más, con padding generoso para que
              respire visualmente. */}
          {activeWorkout.exercises && activeWorkout.exercises.length > 0 && (
            <div className="pt-4 pb-6" data-testid="finish-workout-footer">
              <div className="max-w-2xl mx-auto">
                <Button 
                  onClick={saveWorkout} disabled={saving} data-testid="bottom-finish-btn"
                  className="w-full h-12 text-base font-bold text-white rounded-xl shadow-lg"
                  style={{ backgroundColor: theme.primary }}
                >
                  <FaTrophy className="mr-2" />
                  {saving ? 'Guardando...' : getText('Finalizar Entreno', 'Finalizar Entreno')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN VIEW - Day Selection
  // ============================================================
  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#0a0a0a' }} data-testid="data-progresion-page">
      <div className="fixed inset-0 z-0 pointer-events-none" style={{
        background: `radial-gradient(circle at 80% 20%, ${theme.primary}15, transparent 50%), radial-gradient(circle at 20% 80%, #00F0FF10, transparent 40%)`
      }} />
      
      {/* Header */}
      <div className="relative z-10 text-white py-5 px-6 shadow-md" style={{ background: `linear-gradient(135deg, ${theme.primary}, #00F0FF)` }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white hover:bg-white/20" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <FaArrowLeft />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>Data Progresión</h1>
              <p className="text-white/80 text-sm">{getText('¡Vamos a entrenar, Guerrero!', '¡Vamos a entrenar, Guerrera!')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { icon: FaDumbbell, value: stats.total_workouts, label: 'Entrenamientos', color: theme.primary },
            { icon: FaChartLine, value: stats.unique_exercises, label: 'Ejercicios', color: '#00F0FF' },
            { icon: FaCalendarAlt, value: stats.workouts_this_month, label: 'Este Mes', color: '#CCFF00' },
            { icon: FaFire, value: stats.current_streak, label: 'Racha', color: theme.primary }
          ].map((stat, i) => (
            <div key={i} className="rounded-xl backdrop-blur-xl p-4"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                  <stat.icon className="text-lg" style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Workout Plan */}
        {workoutPlan ? (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>Tu Rutina</h2>
                <p className="text-sm text-gray-500">{workoutPlan.name || workoutPlan.title || 'Mi Rutina'}</p>
              </div>
              <span className="px-3 py-1 rounded-full text-white text-xs font-bold" style={{ backgroundColor: theme.primary }}>
                {(workoutPlan.training_days || workoutPlan.days)?.length || 0} días
              </span>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(workoutPlan.training_days || workoutPlan.days)?.map((day, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08 }}
                >
                  <div 
                    className="cursor-pointer hover:scale-[1.02] transition-all rounded-xl overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    onClick={() => startWorkout(day)}
                    data-testid={`day-card-${idx}`}
                  >
                    <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${theme.primary}, #00F0FF)` }} />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-base font-bold text-white">{day.day_name}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">{day.focus || `${day.exercises?.length || 0} ejercicios`}</p>
                        </div>
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${theme.primary}20` }}>
                          <FaPlay style={{ color: theme.primary }} className="text-xs" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        {day.exercises?.slice(0, 4).map((ex, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#00F0FF' }} />
                            <span className="text-gray-400 truncate">{ex.custom_name || ex.name}</span>
                            <span className="text-gray-600 ml-auto shrink-0">
                              {ex.series?.length || (ex.series_detail?.[0]?.match(/^(\d+)/)?.[1]) || ex.sets || '-'} sets
                            </span>
                          </div>
                        ))}
                        {day.exercises?.length > 4 && (
                          <p className="text-xs text-gray-600 pl-4">+{day.exercises.length - 4} más</p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <Card className="mb-6 border-2 border-dashed" style={{ borderColor: 'rgba(255,255,255,0.15)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
            <CardContent className="py-10 text-center">
              <FaDumbbell className="text-5xl mx-auto mb-3" style={{ color: `${theme.primary}40` }} />
              <h3 className="text-lg font-bold text-white mb-2">Aún no tienes una rutina asignada</h3>
              <p className="text-sm text-gray-500 mb-4">Tu entrenadora te asignará una rutina personalizada muy pronto</p>
            </CardContent>
          </Card>
        )}

        {/* Workout History */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <FaCalendarAlt style={{ color: '#00F0FF' }} /> Historial
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} className="text-gray-500 hover:text-white text-xs">
              {showHistory ? <FaChevronUp /> : <FaChevronDown />}
              <span className="ml-1">{showHistory ? 'Menos' : 'Más'}</span>
            </Button>
          </div>
          
          {recentWorkouts.length === 0 ? (
            <div className="text-center py-10 px-6">
              <FaCalendarAlt className="text-4xl mx-auto mb-3" style={{ color: `${theme.primary}40` }} />
              <p className="text-sm text-gray-400">Aún no has registrado entrenamientos</p>
              <p className="text-xs text-gray-600 mt-1">Inicia tu primer entreno para comenzar</p>
            </div>
          ) : (
            <div>
              {/* Week View */}
              <div className="p-4 border-b" style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="flex justify-between gap-2">
                  {Array.from({ length: 7 }, (_, i) => {
                    const date = new Date();
                    date.setDate(date.getDate() - (6 - i));
                    const dateStr = date.toISOString().split('T')[0];
                    const hasWorkout = recentWorkouts.some(w => new Date(w.workout_date).toISOString().split('T')[0] === dateStr);
                    const isToday = i === 6;
                    
                    return (
                      <div key={i} className="flex flex-col items-center">
                        <span className="text-xs text-gray-600 mb-1">
                          {['D', 'L', 'M', 'X', 'J', 'V', 'S'][date.getDay()]}
                        </span>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                          hasWorkout ? 'text-white' : isToday ? 'border-2 text-gray-400' : 'text-gray-600'
                        }`} style={{ 
                          background: hasWorkout ? `linear-gradient(135deg, ${theme.primary}, #00F0FF)` : 'rgba(255,255,255,0.05)',
                          borderColor: isToday && !hasWorkout ? '#00F0FF' : undefined
                        }}>
                          {date.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Workout List */}
              <div className={showHistory ? '' : 'max-h-60 overflow-y-auto'}>
                {recentWorkouts.map((workout, idx) => (
                  <div key={workout.id || idx} className="p-4 hover:bg-white/3 border-b" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
                            style={{ background: `linear-gradient(135deg, ${theme.primary}, #00F0FF)` }}>
                            {formatDate(workout.workout_date)}
                          </span>
                          {workout.duration_minutes > 0 && (
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <FaClock /> {workout.duration_minutes}m
                            </span>
                          )}
                        </div>
                        <h3 className="text-sm font-semibold text-white">{workout.workout_name}</h3>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {workout.exercises?.slice(0, 3).map((ex, i) => (
                            <span key={i} className="text-xs px-1.5 py-0.5 rounded text-gray-400" style={{ background: 'rgba(255,255,255,0.06)' }}>
                              {ex.exercise_name}
                            </span>
                          ))}
                          {workout.exercises?.length > 3 && (
                            <span className="text-xs text-gray-600">+{workout.exercises.length - 3}</span>
                          )}
                        </div>
                      </div>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ml-3" style={{ background: 'rgba(204,255,0,0.15)' }}>
                        <FaCheck style={{ color: '#CCFF00' }} className="text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Records Modal */}
      <AnimatePresence>
        {showRecordsModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setShowRecordsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
              style={{ backgroundColor: '#1a1a2e' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative p-6 text-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)' }}>
                <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.2, type: "spring" }} className="text-5xl mb-2">
                  <FaTrophy />
                </motion.div>
                <h2 className="text-xl font-bold text-black">NUEVO RÉCORD PERSONAL</h2>
                <p className="text-black/70 text-sm">{getText('¡Lo lograste, campeón!', '¡Lo lograste, campeona!')}</p>
                
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {[...Array(15)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ y: -20, x: Math.random() * 100 - 50, opacity: 1 }}
                      animate={{ y: 200, x: Math.random() * 200 - 100, opacity: 0, rotate: Math.random() * 360 }}
                      transition={{ duration: 2, delay: 0.5 + Math.random() * 0.5 }}
                      className="absolute text-lg"
                      style={{ left: `${Math.random() * 100}%` }}
                    >
                      {['⭐', '🌟', '✨', '💪', '🔥'][Math.floor(Math.random() * 5)]}
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 space-y-3 max-h-[250px] overflow-y-auto">
                {newRecords.map((record, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: -50, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5 + idx * 0.1 }}
                    className="flex items-center gap-3 p-3 rounded-xl"
                    style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.2)' }}
                  >
                    <div className="text-2xl">{record.is_weight_record ? '🏋️' : '💯'}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white text-sm">{record.exercise_name}</h3>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {record.is_weight_record && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                            {record.previous_weight}kg → <span className="font-bold">{record.new_weight}kg</span>
                          </span>
                        )}
                        {record.is_reps_record && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                            {record.previous_reps} → <span className="font-bold">{record.new_reps} reps</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="p-4 border-t border-white/10">
                <Button onClick={() => setShowRecordsModal(false)} className="w-full py-2.5 text-base font-semibold"
                  style={{ background: `linear-gradient(135deg, ${theme.primary}, #00F0FF)` }}>
                  ¡Sigue así! 💪
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DataProgresionNew;
